"use client";

import React, { useEffect, useRef, useState } from "react";
import { Sparkles, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function GamifyingTheFutureOfSustainability() {
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const subtitleItems = [{
    text: "교육부 인가 4년제 정규 사이버대학 · 학업유지율 11년 연속 1위 · 취업률 74.3% · 국가장학금 등록금 전액지원"
  }];
  return <section data-section-id="226655" id="ai-검색-시대의-새로운-기준" className="relative overflow-hidden dark bg-background py-20 lg:py-32">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-[20%] -left-[10%] h-[70%] w-[50%] rounded-lg bg-primary/10 blur-[120px]" />
        <div className="absolute top-[40%] -right-[10%] h-[60%] w-[40%] rounded-lg bg-primary/5 blur-[100px]" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6">
        <div className="flex flex-col items-center justify-center">
          
          <div ref={contentRef} className={`flex flex-col items-center text-center gap-8 transition-all duration-1000 ease-out ${isContentVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
            <div className="flex flex-col items-center gap-6">
              <h1 className="font-default text-4xl font-bold leading-[1.2] tracking-tight md:text-5xl lg:text-5xl max-w-4xl text-center">
                <span className="bg-gradient-to-r from-foreground to-primary bg-clip-text text-transparent">
                  100% 온라인으로 받는 4년제 정규 학위,
                </span>
                <br />
                <span className="text-foreground">건양사이버대학교</span>
              </h1>

              <ul className="flex flex-col gap-3 text-center md:flex md:flex-col md:gap-y-4 md:items-center">
                {subtitleItems.map((item, i) => <li key={i} data-index={i} className="flex items-center justify-center gap-2 text-muted-foreground text-base md:text-lg lg:text-xl">
                    <CheckCircle2 className="size-5 text-primary shrink-0" />
                    <span>{item.text}</span>
                  </li>)}
              </ul>

              
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4 w-full">
              <Button size="lg" className="h-14 w-full sm:w-auto rounded-md px-8 flex items-center justify-center gap-2 text-base font-bold shadow-lg shadow-primary/20 bg-primary text-primary-foreground hover:bg-primary/80" asChild>
                <Link to="https://go.kycu.ac.kr/bbs/jws_reg00.php" newTab>
                  2026학년도 지원서 작성
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="h-14 w-full sm:w-auto rounded-md px-8 flex items-center justify-center gap-2 text-base font-bold border-primary/30 text-foreground hover:bg-primary/10" asChild>
                <Link to="https://go.kycu.ac.kr/bbs/sub_0504.php" newTab>
                  입학 상담 신청
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes float-slow {
          0% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(3deg); }
          100% { transform: translateY(0px) rotate(0deg); }
        }
      `
    }} />
    </section>;
}